<template>
  <div class="mobile-header relative z-50">
    <!-- Logo & Toggle Button -->
    <div class="flex justify-between items-center w-full py-[10px] px-4">
      <a href="/">
        <img :src="logoPath" class="w-[300px]" alt="Insurance Express" />
      </a>
      
      <div @click="toggleMenu" class="hamburger-icon cursor-pointer" :class="{ 'open': isMenuOpen }">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
    
    <!-- Mobile Menu Overlay -->
    <div v-show="isMenuOpen" class="fixed top-0 left-0 w-full h-full bg-black opacity-50 z-40" @click="toggleMenu"></div>
    
    <!-- Mobile Navigation Menu -->
    <nav 
      class="mobile-nav fixed top-0 right-0 h-screen w-[85%] max-w-[320px] bg-white z-50 transition-all duration-300 overflow-y-auto shadow-xl"
      :class="isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'"
    >
      <div class="flex justify-between items-center p-4 border-b border-gray-200">
        <h3 class="font-semibold text-lg text-[#192339]">Navigation</h3>
        <button @click="toggleMenu" class="p-1.5 rounded-full hover:bg-gray-100">
          <Icon icon="tabler:x" width="20" height="20" />
        </button>
      </div>
      
      <ul class="font-merriweather text-[15px]">
        <li v-for="(item, index) in navigationItems" :key="index" class="border-b border-gray-200">
          <!-- Menu item with submenu -->
          <template v-if="item.items">
            <div 
              @click="toggleSubmenu(index)" 
              class="flex justify-between items-center text-[#525355] px-6 py-4 cursor-pointer hover:bg-gray-50"
            >
              <div class="flex items-center gap-2">
                <Icon v-if="item.iconName" :icon="item.iconName" width="20" height="20" />
                <span>{{ item.label }}</span>
              </div>
              <Icon 
                :icon="openSubmenus.includes(index) ? 'tabler:chevron-up' : 'tabler:chevron-down'" 
                width="20" 
                height="20" 
                class="transform transition-transform"
              />
            </div>
            
            <!-- Submenu with icons -->
            <ul v-show="openSubmenus.includes(index)" class="bg-gray-50">
              <li v-for="(subitem, subIdx) in item.items" :key="`${index}-${subIdx}`">
                <a 
                  :href="subitem.to" 
                  :to="subitem.to"
                  class="flex items-center gap-2 py-3 px-8 text-sm hover:text-[#192339] hover:bg-gray-100"
                  :class="{ 'external-link': subitem.type === 'external' }"
                  @click="trackEvent('MobileSubMenu', subitem.label, subitem.type)"
                >
                  <Icon v-if="subitem.iconName" :icon="subitem.iconName" width="18" height="18" />
                  <span>{{ subitem.label }}</span>
                </a>
              </li>
            </ul>
          </template>
          
          <!-- Regular menu item -->
          <template v-else>
            <a 
              :href="item.to" 
              :to="item.to"
              class="flex items-center gap-2 text-[#525355] px-6 py-4 hover:text-[#192339] hover:bg-gray-50"
              @click="trackEvent('MobileMenu', item.label, item.type)"
            >
              <Icon v-if="item.iconName" :icon="item.iconName" width="20" height="20" />
              <span>{{ item.label }}</span>
            </a>
          </template>
        </li>
      </ul>
      
      <!-- Contact Info -->
      <div class="mt-6 px-6">
        <h3 class="font-semibold text-lg mb-2">Contact Us</h3>
        <div class="flex items-center space-x-4 mt-4">
          <a :href="`tel:${contactInfo.phone}`" class="contact-icon text-red-500">
            <Icon icon="tabler:phone" width="24" height="24" />
          </a>
          <a :href="`mailto:${contactInfo.email}`" class="contact-icon text-blue-700">
            <Icon icon="tabler:mail" width="24" height="24" />
          </a>
          <a :href="contactInfo.mapLink" target="_blank" rel="noopener noreferrer" class="contact-icon text-green-600">
            <Icon icon="tabler:map-pin" width="24" height="24" />
          </a>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { mainMenuItems, contactInfo } from '@/data/navigation';
import { Icon } from '@iconify/vue';
import logoPath from '@assets/images/brand/logos/insurance_express-main-logo.svg';

export default {
  name: 'AppHeaderMobile',
  components: {
    Icon
  },
  data() {
    return {
      isMenuOpen: false,
      openSubmenus: [],
      navigationItems: mainMenuItems,
      contactInfo,
      logoPath
    };
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      
      // Prevent scrolling when menu is open
      if (this.isMenuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    toggleSubmenu(index) {
      if (this.openSubmenus.includes(index)) {
        this.openSubmenus = this.openSubmenus.filter(i => i !== index);
      } else {
        this.openSubmenus.push(index);
      }
    },
    trackEvent(category, label, type) {
      // Close menu if this is a regular link
      if (!this.openSubmenus.includes(label)) {
        this.isMenuOpen = false;
        document.body.style.overflow = '';
      }
      
      // Track analytics
      if (window.gtag) {
        window.gtag('event', 'menu_click', {
          'event_category': category,
          'event_label': label,
          'link_type': type
        });
      }
    }
  }
};
</script>

<style scoped>
.contact-icon {
  transition: transform 0.2s ease;
}

.contact-icon:hover {
  transform: scale(1.2);
}

.menu-toggle {
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

/* Hamburger Menu Animation */
.hamburger-icon {
  width: 20px;
  height: 16px;
  position: relative;
  transform: rotate(0deg);
  transition: .5s ease-in-out;
  cursor: pointer;
}

.hamburger-icon span {
  display: block;
  position: absolute;
  height: 2px;
  width: 100%;
  background: #192339;
  border-radius: 2px;
  opacity: 1;
  left: 0;
  transform: rotate(0deg);
  transition: .25s ease-in-out;
}

.hamburger-icon span:nth-child(1) {
  top: 0px;
}

.hamburger-icon span:nth-child(2) {
  top: 7px;
}

.hamburger-icon span:nth-child(3) {
  top: 14px;
}

.hamburger-icon.open span:nth-child(1) {
  top: 7px;
  transform: rotate(135deg);
}

.hamburger-icon.open span:nth-child(2) {
  opacity: 0;
  left: -60px;
}

.hamburger-icon.open span:nth-child(3) {
  top: 7px;
  transform: rotate(-135deg);
}
</style> 